import React, { useLayoutEffect, useRef, useState } from 'react';
import { Rune, setName, EFF_LABEL_GAME, SET_EFFECT, QUALITY, GRIND_MAX } from './runeEngine';
import { glyphSrc } from './RuneStar';

/** 자리 모양 룬돌 — SWARFARM 이 공개한 그림(1·4번은 세로로 긴 돌, 나머지는 옆으로 긴 돌) */
const plateSrc = (slot: number) => `/img/runes/rune${slot}.png`;

/**
 * 게임 룬 파츠의 별 — 자홍색 오각별에 어두운 테두리, 위쪽이 밝다. 글자 ★ 는 보라로 보여서
 * 게임 화면에서 색을 재어(채움 (209,61,180)·밝은 곳 (233,97,231)·테두리 (129,60,103)) 그렸다.
 * 별끼리 살짝 겹친다.
 */
function Stars({ n, size }: { n: number; size: number }) {
    const pts = '10,1.5 12.6,7.3 19,7.9 14.2,12.1 15.6,18.4 10,15.1 4.4,18.4 5.8,12.1 1,7.9 7.4,7.3';
    return (
        <div style={{ display: 'flex', justifyContent: 'center' }}>
            {Array.from({ length: n }).map((_, i) => (
                <svg key={i} viewBox="0 0 20 20" width={size} height={size} style={{ marginLeft: i ? -size * 0.18 : 0, overflow: 'visible' }}>
                    <defs>
                        <linearGradient id="rune-star" x1="0" y1="0" x2="0" y2="1">
                            <stop offset="0" stopColor="#f07ae6" />
                            <stop offset="0.45" stopColor="#d43db4" />
                            <stop offset="1" stopColor="#b8258f" />
                        </linearGradient>
                    </defs>
                    <polygon points={pts} fill="url(#rune-star)" stroke="#4a1f3d" strokeWidth={1.6} strokeLinejoin="round" />
                </svg>
            ))}
        </div>
    );
}

/**
 * **룬 창 — 게임에서 룬을 눌렀을 때 뜨는 그 판, 배치 그대로.**
 *
 *                +15 칼날의 룬 (3)              [X]
 *   ┌판 그림┐  방어력 +160                     ┌ 전설 ┐
 *   │★★★★★★│
 *   │ 문양 +15│
 *   └────────┘
 *   치명 피해 +11%
 *   방어력 +13%+6%♻ (최대 +10%)
 *   공격속도 +11+4
 *   치명 확률 +17%
 *   2세트 : 치명 확률 +12%
 *
 * 부옵션은 **원래 값과 연마를 따로** 적는다(연마는 게임처럼 금색). 보석으로 바꾼 옵션에는 게임의
 * 변환 아이콘(주황 화살표, `gem.png`)을 붙이고, 연마 자체는 따로 표시하지 않는다. 그 옆 파란 글씨는
 * 이 룬을 끝까지 키웠을 때 그 자리가 어떻게 되나 — 종류가 그대로면 더 올릴 연마량 "(+N)",
 * **갈아엎을 자리면 무엇이 되는지 "→ 공격력 +23%"**. 바뀔 것이 없으면 안 적는다.
 * 접두옵션은 주옵션 아래 흰 글씨로 조금 작게. 글씨는 가는 굵기에 어두운 테두리.
 * 게임의 연필·공유·[임시 각인][강화][판매]·판매가·끼고 있는 몹은 뺐다(사용자 지시).
 */

const GEM_ICON = '/img/runes/gem.png';

/** 게임 글씨 — 가는 굵기에 어두운 테두리 */
const OUTLINE = '-1px 0 #1a0f08, 1px 0 #1a0f08, 0 -1px #1a0f08, 0 1px #1a0f08, -1px -1px #1a0f08, 1px 1px #1a0f08, -1px 1px #1a0f08, 1px -1px #1a0f08';
const gameText = (size: number, color: string, weight = 500): React.CSSProperties => ({
    fontSize: size, color, fontWeight: weight, textShadow: OUTLINE, letterSpacing: -0.2
});

const SET_FILE: Record<number, string> = {
    1: 'energy', 2: 'guard', 3: 'swift', 4: 'blade', 5: 'rage', 6: 'focus', 7: 'endure', 8: 'fatal',
    10: 'despair', 11: 'vampire', 13: 'violent', 14: 'nemesis', 15: 'will', 16: 'shield', 17: 'revenge',
    18: 'destroy', 19: 'fight', 20: 'determination', 21: 'enhance', 22: 'accuracy', 23: 'tolerance',
    24: 'seal', 25: 'intangible'
};

const pct = (type: number) => type === 2 || type === 4 || type === 6 || type >= 9;
const unit = (type: number) => (pct(type) ? '%' : '');
const statText = (type: number, value: number) => `${EFF_LABEL_GAME[type] || `옵션${type}`} +${value}${unit(type)}`;

/** 게임 창의 폭 — 아래 치수는 전부 이 폭에서 잰 px 이고, 실제 폭에 맞춰 통째로 줄인다 */
const GAME_W = 1026;

export default function RuneDetail({ rune, peak, isMobile, onClose, width, bare = false, asIs = false }: {
    rune: Rune;
    /** 같은 룬을 끝까지 키운 것 — 파란 글씨(연마 여유·변환 예정)를 낼 때 */
    peak?: Rune;
    /**
     * **있는 그대로만** — 파란 글씨(변환 예정·연마 여유)를 그리지 않는다. 전투기록에서 그 전투에
     * 쓴 룬을 보여 줄 때 쓴다(사용자 지시: 키울 여지가 아니라 룬 그대로). 금색 연마값·보석
     * 표시는 룬에 실제로 들어 있는 것이라 그대로 둔다.
     */
    asIs?: boolean;
    isMobile: boolean;
    onClose?: () => void;
    /** 바깥 판이 이미 어두운 바탕이면 바탕·테두리·여백 없이 안쪽만 그린다 (별 옆에 나란히 놓을 때) */
    bare?: boolean;
    /** 폭을 알고 있으면(서버 렌더 등) 재지 않고 쓴다 */
    width?: number;
}) {
    const q = QUALITY[rune.quality] || QUALITY[5];
    const file = SET_FILE[rune.set];

    // 카드 폭을 재서 배율을 정한다 — 폰이든 데스크톱이든 게임 창 비율 그대로 들어간다
    const box = useRef<HTMLDivElement | null>(null);
    const [measured, setMeasured] = useState<number>(width || (isMobile ? 340 : 480));
    useLayoutEffect(() => {
        if (width || !box.current) return;
        const el = box.current;
        const read = () => setMeasured(el.clientWidth || (isMobile ? 340 : 480));
        read();
        const ro = typeof ResizeObserver !== 'undefined' ? new ResizeObserver(read) : null;
        ro?.observe(el);
        return () => ro?.disconnect();
    }, [width, isMobile]);
    const f = Math.max(0.3, Math.min(1, measured / GAME_W));
    // 폰은 글자 최소 크기도 조금 낮춘다 — 좁은 칸에서 최소값에 걸려 줄지 않아 [스탯|룬] 칸이 길어졌다(사용자: 창 아래 여백)
    const mn = isMobile ? 0.86 : 1;

    return (
        <div ref={box} style={{
            position: 'relative', boxSizing: 'border-box', width: '100%', color: '#f4efe6', fontFamily: 'inherit',
            // 바깥 카드가 가운데 정렬이면 여기까지 흘러들어 짧은 줄(접두옵션)이 가운데로 밀린다
            textAlign: 'left',
            ...(bare ? {} : {
                background: 'linear-gradient(180deg, #2f1f16 0%, #23160f 100%)',
                border: '1px solid #5a3d24', borderRadius: 16, padding: `${10 * f}px ${14 * f}px ${14 * f}px`,
                boxShadow: '0 8px 20px rgba(0,0,0,0.35)'
            })
        }}>
            {/* ── 제목 줄: 연필·공유 (모양만) / 제목 / 닫기 ── */}
            <div style={{ display: 'grid', gridTemplateColumns: `${44 * f}px 1fr ${44 * f}px`, alignItems: 'center' }}>
                <span />
                <div style={{ textAlign: 'center', whiteSpace: 'nowrap', ...gameText(Math.max(13 * mn, 30 * f), '#f2b23a', 600) }}>
                    +{rune.up} {setName(rune.set)}의 룬 ({rune.slot})
                </div>
                <button
                    onClick={onClose} title="닫기"
                    style={{ justifySelf: 'end', width: 40 * f, height: 40 * f, border: 'none', background: 'transparent', color: '#e9c98a', fontSize: 34 * f, fontWeight: 900, lineHeight: 1, cursor: 'pointer', padding: 0 }}
                >×</button>
            </div>

            {/* ── 몸통: 왼쪽(판 + 주옵션 + 부옵션 + 세트) / 오른쪽(등급 · 판매가 · 단추) ── */}
            {/* 폰에서는 등급 띠(전설·영웅…)를 감춘다 — 좁은 줄에서 자리만 먹는다(사용자 지시) */}
            <div style={{ display: 'grid', gridTemplateColumns: isMobile ? '1fr' : `1fr ${200 * f}px`, columnGap: 18 * f, marginTop: 10 * f }}>
                <div>
                    <div style={{ display: 'flex', gap: 18 * f, alignItems: 'flex-start' }}>
                        {/*
                          판 그림 — 게임의 룬 파츠처럼 **자리 모양 룬돌**(1·4번은 세로로 긴 돌, 2·3·5·6번은 옆으로
                          긴 돌)에 문양을 얹는다. 위에 성급, 오른쪽 아래에 +강화. 주황 바탕은 뺐지만 네모 테두리는 둔다
                          (사용자 지시). 크기는 한 변 150px(게임 창 기준) — 180 은 커 보여 줄였다.
                        */}
                        <div style={{
                            position: 'relative', width: 150 * f, height: 150 * f, flexShrink: 0, borderRadius: 12,
                            // 네모 테두리는 둔다(사용자) — 주황 바탕만 뺐다
                            border: `${3 * f}px solid #8a5a2a`, background: 'rgba(0, 0, 0, 0.22)', boxShadow: 'inset 0 0 10px rgba(0,0,0,0.45)', overflow: 'hidden'
                        }}>
                            {/*
                              룬돌은 별 바로 아래에 **위 끝을 맞춰** 놓는다. 정사각 상자에 가운데 맞춤으로 두면
                              그림(1·4번 42×50, 나머지 50×38)의 빈 여백 때문에 별과 돌 사이가 벌어졌다.
                              문양은 돌을 감싼 상자 가운데에 놓아 돌 모양과 무관하게 한가운데에 온다.
                            */}
                            <div style={{ position: 'absolute', left: 0, right: 0, top: 25 * f, display: 'flex', justifyContent: 'center', alignItems: 'flex-start' }}>
                                <div style={{ position: 'relative', display: 'block' }}>
                                    {/* 그림은 50×38(옆으로 긴 돌) / 42×50(세로로 긴 돌) 이라 자연 크기로 두면 작다 — 자리에 따라 폭·높이를 준다 */}
                                    <img loading="lazy" src={plateSrc(rune.slot)} alt="" aria-hidden
                                        style={{
                                            display: 'block', filter: 'drop-shadow(0 2px 3px rgba(0,0,0,0.6))',
                                            ...(rune.slot === 1 || rune.slot === 4
                                                ? { height: 112 * f, width: 'auto' }
                                                : { width: 122 * f, height: 'auto' })
                                        }} />
                                    {file && (
                                        <img loading="lazy" src={glyphSrc(file)} alt={setName(rune.set)}
                                            style={{
                                                position: 'absolute', left: '50%', top: '50%', width: 54 * f, height: 54 * f, transform: 'translate(-50%, -50%)', objectFit: 'contain',
                                                // 문양에 어두운 테두리 — 작게 줄이면 그림 속 테두리가 흐려져서 덧그린다
                                                filter: 'drop-shadow(1px 0 0 #3a1a05) drop-shadow(-1px 0 0 #3a1a05) drop-shadow(0 1px 0 #3a1a05) drop-shadow(0 -1px 0 #3a1a05)'
                                            }} />
                                    )}
                                </div>
                            </div>
                            <div style={{ position: 'absolute', top: 2 * f, left: 0, right: 0 }}>
                                <Stars n={Math.max(1, Math.min(6, rune.stars || 6))} size={22 * f} />
                            </div>
                            <div style={{
                                position: 'absolute', right: 6 * f, bottom: 2 * f, ...gameText(28 * f, '#fff', 600)
                            }}>+{rune.up}</div>
                        </div>

                        {/* 주옵션 (+ 접두옵션) */}
                        <div style={{ paddingTop: 22 * f, minWidth: 0 }}>
                            <div style={{ lineHeight: 1.15, whiteSpace: 'nowrap', ...gameText(Math.max(15 * mn, 40 * f), '#fff') }}>
                                {statText(rune.main[0], rune.main[1])}
                            </div>
                            {rune.prefix && (
                                <div style={{ marginTop: 4 * f, ...gameText(Math.max(12 * mn, 26 * f), '#f4efe6') }}>
                                    {statText(rune.prefix[0], rune.prefix[1])}
                                </div>
                            )}
                        </div>
                    </div>

                    {/* 부옵션 — 원래 값 + 연마(금색 ♻) + 최대 연마(파랑) */}
                    <div style={{ marginTop: 16 * f, display: 'flex', flexDirection: 'column', gap: 4 * f }}>
                        {rune.subs.map(([t, v, g, gem], i) => {
                            const base = v - g;
                            const max = GRIND_MAX[t] ? GRIND_MAX[t][rune.ancient ? 1 : 0] : null;
                            const room = !asIs && max !== null ? Math.max(0, max - g) : 0;   // 더 올릴 수 있는 연마량
                            // 끝까지 키우면 이 자리가 무엇이 되나 — 종류가 바뀌면 변환할 자리다
                            const after = asIs ? undefined : peak?.subs[i];
                            const converts = !!after && after[0] !== t;
                            return (
                                <div key={i} style={{ lineHeight: 1.25, display: 'flex', alignItems: 'center', gap: 6 * f, flexWrap: 'wrap', ...gameText(Math.max(12 * mn, 30 * f), '#f4efe6') }}>
                                    <span>{EFF_LABEL_GAME[t] || `옵션${t}`} +{base}{unit(t)}</span>
                                    {g > 0 && <span style={{ color: '#f2b23a' }}>+{g}{unit(t)}</span>}
                                    {gem > 0 && (
                                        <img loading="lazy" src={GEM_ICON} alt="변환" title="보석으로 바꾼 옵션"
                                            style={{ width: Math.max(12 * mn, 26 * f), height: Math.max(12 * mn, 26 * f), objectFit: 'contain' }} />
                                    )}
                                    {converts ? (
                                        <span title="끝까지 키우면 이 자리를 보석으로 갈아엎는다" style={{ ...gameText(Math.max(11 * mn, 24 * f), '#5cc8ff') }}>
                                            → {statText(after![0], after![1])}
                                        </span>
                                    ) : room > 0 ? (
                                        <span title="더 올릴 수 있는 연마량" style={{ ...gameText(Math.max(11 * mn, 24 * f), '#5cc8ff') }}>
                                            (+{room}{unit(t)})
                                        </span>
                                    ) : null}
                                </div>
                            );
                        })}
                    </div>

                    {/* 세트 효과 */}
                    <div style={{ marginTop: 18 * f, textAlign: 'left', ...gameText(Math.max(12 * mn, 30 * f), '#b6e85a') }}>
                        {SET_EFFECT[rune.set] || `${setName(rune.set)} 세트`}
                    </div>
                </div>

                {/* 오른쪽 기둥 — 등급 띠 */}
                {!isMobile && (
                    <div style={{ paddingTop: 6 * f }}>
                        <div style={{
                            height: 52 * f, borderRadius: 10, background: 'linear-gradient(180deg, #7a3f1f 0%, #5a2c14 100%)', border: '2px solid #8a4d22',
                            display: 'flex', alignItems: 'center', justifyContent: 'center', ...gameText(Math.max(11 * mn, 26 * f), q.color, 600)
                        }}>{rune.ancient ? '고대 ' : ''}{q.name}</div>
                    </div>
                )}
            </div>
        </div>
    );
}
